<template>
  <div class="screening-questions-container" dir="rtl">
    <label class="label">שאלות למועמד בצ'אט</label>
    <p class="help">עד 6 שאלות, שאלה אחת בכל שורה. הבוט ישאל אותן לפני בדיקת ההתאמה.</p>

    <div v-if="draft.length" class="sq-head">
      <span class="sq-head-cell sq-h-text">שאלה</span>
      <span class="sq-head-cell sq-h-expected">תשובה רצויה <span class="sq-head-hint">איזה תשובה עוברת?</span></span>
      <span class="sq-head-cell sq-h-must"></span>
      <span class="sq-h-remove"></span>
    </div>

    <div v-for="(q, i) in draft" :key="q.id" class="sq-row">
      <div class="sq-line">
        <input
          class="input is-small sq-text"
          :class="{ 'is-danger': showErrors && errors[i] }"
          type="text"
          v-model="q.text"
          placeholder="האם עבדת בעבר עם תוכנת …?"
          @input="markDirty"
        />
        <select class="sq-expected" v-model="q.expected" @change="onExpectedChange(q)" title="תשובה רצויה">
          <option value="yes">כן</option>
          <option value="no">לא</option>
          <option value="any">הכל עובר, רק אוספים מידע</option>
        </select>
        <label class="checkbox sq-must">
          <input type="checkbox" v-model="q.must" :disabled="q.expected === 'any'" @change="markDirty" />
          תנאי חובה
        </label>
        <button type="button" class="button is-small is-light sq-remove" title="הסרת שאלה" @click="removeRow(i)">×</button>
      </div>
      <p v-if="showErrors && errors[i]" class="help is-danger">{{ errors[i] }}</p>
    </div>

    <div class="sq-actions">
      <button type="button" class="button is-small is-primary" :disabled="saving || !dirty" @click="save">
        {{ saving ? 'שומר…' : 'שמירת שאלות' }}
      </button>
      <button v-if="draft.length < max" type="button" class="button is-small is-light" @click="addRow">הוספת שאלה</button>
      <span v-if="status === 'saved'" class="sq-status sq-saved">נשמר ✓</span>
      <span v-if="status === 'error'" class="sq-status sq-error">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import { MAX_QUESTIONS, newQuestion, validateQuestions } from '@/custom/helpers/screening_questions'

export default {
  name: 'PositionScreeningQuestions',
  props: {
    positionId: { type: [Number, String], required: true },
    questions: { type: Array, default: () => [] },
    revision: { type: Number, default: 0 }
  },
  data () {
    return {
      draft: [],
      dirty: false,
      saving: false,
      showErrors: false,
      status: '',
      errorMessage: '',
      max: MAX_QUESTIONS
    }
  },
  computed: {
    errors () {
      return validateQuestions(this.draft).errors
    }
  },
  watch: {
    // Server-side change (reload / revision bump) replaces the draft unless the user is mid-edit.
    revision () { if (!this.dirty) this.resetDraft() },
    questions () { if (!this.dirty) this.resetDraft() }
  },
  created () {
    this.resetDraft()
  },
  methods: {
    resetDraft () {
      this.draft = (this.questions || []).slice(0, MAX_QUESTIONS).map(q => ({
        id: q.id,
        text: q.text || '',
        expected: q.expected || 'yes',
        must: !!q.must
      }))
      this.dirty = false
    },
    markDirty () {
      this.dirty = true
      this.status = ''
    },
    addRow () {
      if (this.draft.length >= MAX_QUESTIONS) return
      this.draft.push(newQuestion())
      this.markDirty()
    },
    removeRow (i) {
      this.draft.splice(i, 1)
      this.markDirty()
    },
    onExpectedChange (q) {
      if (q.expected === 'any') q.must = false
      this.markDirty()
    },
    async save () {
      if (this.saving) return
      const result = validateQuestions(this.draft)
      if (!result.valid) {
        this.showErrors = true
        this.status = ''
        return
      }
      this.showErrors = false
      this.saving = true
      this.status = ''
      try {
        await this.$network_utils.updatePosition({
          position_id: this.positionId,
          screening_questions: result.rows
        })
        this.draft = result.rows.map(r => Object.assign({}, r))
        this.dirty = false
        this.status = 'saved'
        this.$emit('saved', result.rows)
      } catch (error) {
        const data = error && error.response && error.response.data
        const serverMsg = data && (data.error || data.message || (Array.isArray(data.errors) ? data.errors.join(', ') : data.errors))
        this.errorMessage = (typeof serverMsg === 'string' && serverMsg) || 'השמירה נכשלה, אפשר לנסות שוב'
        this.status = 'error'
        this.$utils.emit('show_toast', { text: this.errorMessage })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.screening-questions-container {
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  background-color: #fafafa;
  text-align: right;
}
.label { font-weight: bold; color: #333; margin-bottom: 2px; }
.help { font-size: 0.8em; margin-bottom: 6px; }
.sq-row { margin-bottom: 6px; }
.sq-line { display: flex; align-items: center; gap: 6px; }
.sq-text { flex: 1; }
.sq-expected, .sq-h-expected { width: 180px; flex: 0 0 180px; }
.sq-expected { font-size: 0.85rem; }
.sq-must, .sq-h-must { width: 84px; flex: 0 0 84px; }
.sq-must { font-size: 0.8rem; white-space: nowrap; }
.sq-remove, .sq-h-remove { width: 28px; flex: 0 0 28px; }
.sq-h-text { flex: 1; }
.sq-head { display: flex; align-items: flex-end; gap: 6px; margin-bottom: 3px; }
.sq-head-cell { font-size: 0.78rem; font-weight: bold; color: #555; }
.sq-head-hint { font-weight: normal; color: #888; }
.sq-actions { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.sq-status { font-size: 0.8rem; }
.sq-saved { color: #257942; }
.sq-error { color: #cc0f35; }
</style>
